import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, DatePicker, Input, InputNumber, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';

import ItemLookupSelect from './ItemLookupSelect';
import IdValueCheck from './IdValueCheck';
import FilterIdsLine from './FilterIdsLine';
import { CopyTextButton } from './ShowAsText';
import TagSelect from '../tags/TagSelect';
import UserPickerSelect from '../users/UserPickerSelect';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import useCollaborators from '../../hooks/useCollaborators';
import MeasurementValueInput from './MeasurementValueInput';
import CurrencyValueInput from './CurrencyValueInput';
import { buildChoiceOptions, rendersAsChoice } from './ChoiceOptions';
import { CLOSE, PLUS } from '../icons';
import { optionSearchFilter } from '../Input';
import { getDictionaryFromName } from '../../utils/dictionary';
import { booleanLabels, getNumericInputMode } from '../../utils/elements';
import { parseNumberEntry } from '../../utils/numberEntry';
import { normalizeId } from '../../utils/ids';
import { ME_VALUE } from '../../utils/userElements';
import {
	bool as kqlBool,
	cmp as kqlCmp,
	date as kqlDate,
	list as kqlList,
	me as kqlMe,
	num as kqlNum,
	parse as kqlParse,
	serialize as kqlSerialize,
	str as kqlStr,
	SYSTEM_FIELDS,
	ELEMENT_ID_RE,
	BLOCK_CMP,
	ENTRY_COUNT,
} from '../../utils/kql';
import {
	DATE_PART_TYPE,
	elementFieldEntries,
	entriesNode,
	entriesRowOf,
	entryColumnFields,
	findField,
	isEntriesCmp,
	isListOperator,
	isNullaryOperator,
	newEntryRowFor,
	pathKey,
	queryUsesMe,
	rawValueOf,
	seedRowsForGroup,
	tagsFilterField,
} from '../../utils/kqlBuilder';
import { conditionOperatorOptions, conditionOperatorValues } from '../../utils/conditionOperators';
import {
	GROUP_MATCHES,
	MATCH_ALL,
	MATCH_ANY,
	MATCH_NONE,
	TOP_MATCHES,
	allRows,
	groupMatchFor,
	isGroupItem,
	makeGroup,
	mapRows,
	readGrouped,
	removeRow,
	updateGroup,
	writeGrouped,
} from '../../utils/conditionGroups';
import { conditionSentence } from '../../utils/drivenSummary';
import { REPEATING_GROUP } from '../../config/repeatingGroups';
import { TAG_FIELD_KEY } from '../../utils/tagNames';
import { isMeasurementValue } from '../../utils/units';
import { formatEnteredMeasurement } from '../../utils/measurements';
import { isCurrencyValue } from '../../utils/currency';
import { currencyLiteral } from '../../utils/money';
import {
	checkConditionValues,
	checkOperator,
	checkQueryOperators,
	checkQueryValues,
	displayedCheck,
	idValueKindFor,
	isFlagged,
	replaceQueryValue,
	resolveQueryLabels,
	resolveQueryTextLabels,
	userVocabularyOf,
} from '../../utils/idValueValidation';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * ConditionBuilder — a visual editor for the conditions automations run on.
 *
 * KQL REMAINS THE STORAGE FORMAT. This component is a lens over a KQL string,
 * never a replacement for it: it parses the incoming text with the vendored
 * grammar (`utils/kql`), shows what it can as rows, and serializes rows back
 * through the same grammar's AST constructors so the text it emits is exactly
 * the canonical form the API compiles. Nothing here hand-builds query text.
 *
 * WHY there is a text mode. The grammar is strictly larger than the builder:
 * groups inside groups, `not (a and b)`, dot-walked paths, `in (...)` on a
 * field that offers no list. A builder that quietly re-serialized what it
 * understood would silently DELETE the half it didn't — a manager's
 * `a = "1" or (b = "2" and (c = "3" or d = "4"))` would come back shorter. So
 * anything the rows cannot show opens as a read-only sentence (or, when it
 * does not read, as the text) and passes the string through untouched.
 * Losing an editing affordance is a bug; losing a manager's query is data loss.
 *
 * ONE LEVEL OF GROUPS (KPR-951). The top line reads "Match [all | any] of
 * these" (`and` / `or`), and "Add a group" gathers rows one level down, each
 * group with its own all / any / none (`(a and b)`, `(a or b)`, `not (a or b)`).
 * The shape is utils/conditionGroups.js, which FilterBuilder shares. A query
 * deeper than that READS as a plain sentence, read-only, with Edit as text;
 * the builder never rewrites what it cannot show.
 *
 * Public API — the integration pass builds against this:
 *   value      KQL string ('' when there are no conditions)
 *   onChange   (nextKqlString) => void — canonical text from the builder, or
 *              the user's verbatim text in Advanced mode
 *   elements   merged element defs of the subject card:
 *              [{ name, label: {singular}, dataType, options }]
 *   collectionId optional; the collection whose items a card-lookup value
 *              picker offers. Without it a lookup row keeps a text input.
 *   disabled   optional; makes the whole builder read-only
 *   tagDisabledFor optional; `(tag) => reason | null` greys a tag in the
 *              Tags row's picker with its reason (an automatic tag's rule
 *              can't read its own tag — components/tags/TagRuleEditor)
 *   systemFields optional, default true; false offers the elements alone (no
 *              created / updated / tags) — a repeating group entry's own
 *              condition (KPR-687, AutomationRuleEditor)
 *   onEditAsText optional; `(text) => void` takes "Edit as text" to the host's
 *              own text view (KPR-950, cards/DrivenWhereEditor)
 *   allowMe    optional, default false (KPR-143); true offers "Me" in a person
 *              row and opens a stored `me` on a user element as a row. Only a
 *              host whose condition is read by the person looking at it passes
 *              it (the worklist, a multi add seed): a rule runs with nobody
 *              signed in and the API refuses `me` in one. Without it a
 *              condition naming `me` opens as the read-only sentence with
 *              Edit as text, and says why (`meReason` words it per host)
 *
 * The element vocabulary still arrives as a prop, so this renders inside a
 * modal, a tab, or a test with no providers at all. The ONE thing that reaches
 * outside is the card-lookup value picker, which fetches the linked card's
 * items (see ItemLookupSelect): a lookup condition stores a 24-hex item id, and
 * asking a manager to type one — or to recognize one on re-open — is exactly
 * the affordance this builder exists to remove. (A user element's typed value
 * is also checked against the collection's collaborators, fetched only when a
 * row holds one — see below.)
 *
 * EVERY VALUE WITH AN ID BEHIND IT IS CHECKED (ruling d21,
 * utils/idValueValidation.js) — in a row and in the text mode alike: a choice
 * that is not one of the element's choices, an item id nobody can find, a
 * name typed on a user element. Each is flagged under its value with the
 * matches offered, one click each. A lookup's typed text is a title match by
 * design and is only noted. Nothing here holds Save — the API accepts all of
 * these.
 *
 * A NAME THAT STANDS FOR ONE STORED VALUE IS STORED AS THAT VALUE (ruled b01,
 * 2026-09-30: the condition builder keeps ids and choice values, never the
 * words for them) — a choice's label becomes its value, a tag's name, alias
 * or path its id, exactly as the filter builder writes them at Apply. The
 * rule is the validator's (`resolveQueryLabels`, one rule for both
 * builders); this builder only applies it: to its rows (which then show the
 * choice or tag picked, with nothing to say), and to the text mode's OUTPUT
 * — the box keeps what was typed and notes what saving stores. A stored rule
 * that names one is handed back mapped as soon as the vocabulary to map it by
 * is here (a tag's after the collection's tags arrive), so the next Save
 * stores the value; a rule with nothing to map is never written by opening
 * it. An unknown or ambiguous value stays as written, flagged.
 */

const SYSTEM_FIELD_SET = new Set(SYSTEM_FIELDS);

/** System fields offered beside the card's own elements. `card` is not one:
 *  the subject card is already chosen by the surface embedding this. */
export const SYSTEM_CONDITION_FIELDS = [
	{ key: 'created', path: ['created'], label: 'Created', dataType: 'date-time', system: true },
	{ key: 'updated', path: ['updated'], label: 'Updated', dataType: 'date-time', system: true },
	// `tags` joins these where the builder knows its collection
	// (buildConditionFields' `tagsField`): a condition is SHARED text, so it
	// is the collection-mode field — the collection's tags and item tags, by
	// id, never a private tag (docs/plans/tags.md §3.7).
];

/**
 * Field SHAPE -> operator/value family. Kept local rather than shared with
 * `kqlBuilder.fieldKindFor` because the automation vocabulary is deliberately
 * narrower (no relative-date presets) and
 * phrased differently ("on / before / after" reads better than "> / <" on a
 * date). The comparisons each kind offers are NOT kept here: they are the
 * filter builder's table plus the differences listed in
 * utils/conditionOperators.js (KPR-949).
 *
 * Choices are asked about through the shared predicate, which is a Choice
 * element's alone (KPE-033): a short text, number or date carrying a list kept
 * from an earlier life as a Choice is still a text, number or date, and keeps
 * its own operators and control. See ChoiceOptions.js.
 */
export function conditionKindFor(field) {
	if (field?.kind) return field.kind;
	// A repeating group is asked about its ENTRIES (KPR-863): is there one where
	// these hold together, or none, or any at all.
	if (field?.dataType === REPEATING_GROUP) return 'entries';
	if (field?.dataType === 'tag') return 'tag';
	// A part of a date (`created.weekday`): one of its values, picked.
	if (field?.dataType === DATE_PART_TYPE) return 'datePart';
	if (rendersAsChoice(field)) return 'choice';
	switch (field?.dataType) {
		// A "Me too" count (KPR-390) conditions as the number it is — never
		// `contains`, which the API refuses on one.
		case 'number':
		case 'decimal':
		case 'integer':
		case 'rating':
		case 'confirmations':
			return 'number';
		case 'date':
		case 'date-time':
		case 'time':
			return 'date';
		case 'choice':
			return 'choice';
		case 'card-lookup':
			return 'lookup';
		case 'measurement':
			return 'measurement';
		case 'currency':
			return 'currency';
		case 'boolean':
			return 'boolean';
		// A person is picked, never typed (KPR-143): the API matches an
		// account id (or `me`, where the host allows it) and no name.
		case 'user':
			return 'user';
		// A file element (PR 10b): a rule asks only whether it holds a file.
		case 'file':
			return 'file';
		// The text-adjacent types condition as TEXT, the same as they filter
		// (utils/kqlBuilder fieldKindFor): a phone and an email are strings,
		// and a location is matched over its address by the API's compiler.
		case 'phone':
		case 'email':
		case 'location':
			return 'text';
		default:
			return 'text';
	}
}

// THE OPERATORS come from the filter builder's own table
// (utils/kqlBuilder.js OPERATORS_BY_KIND), less and plus the differences listed
// in ONE place, utils/conditionOperators.js (KPR-949). Every comparison is one
// the KQL AST already supports; the builder invents no vocabulary the API
// cannot compile.

/** Operator Select options legal for a field. */
export function operatorsForField(field) {
	return conditionOperatorOptions(conditionKindFor(field), field);
}

/**
 * The card's elements plus the system fields, as builder field descriptors.
 * `tagsField` (utils/kqlBuilder tagsFilterField, collection mode) adds Tags.
 */
export function buildConditionFields(elements = [], { tagsField = null, systemFields = true } = {}) {
	const seen = new Set();
	const fields = [];
	(elements || []).forEach(element => {
		const name = element?.name;
		if (!name || seen.has(name)) return;
		seen.add(name);
		// An element literally named `tags`/`created`/… only means the element
		// when its first path segment is quoted; the grammar calls that
		// forceElement and the serializer re-emits the quotes.
		const forceElement = SYSTEM_FIELD_SET.has(name);
		const label = element?.label?.singular || name;
		const dataType = element?.dataType || 'text-small';
		const options = element?.options || {};
		const id = ELEMENT_ID_RE.test(String(element?.id || '')) ? element.id : null;
		const nameKey = pathKey([name], forceElement);
		fields.push({
			// A condition names the element ITSELF (element ids step 3,
			// KPR-238 P4): one card's element is one element — its name is
			// unique on the card — so a row picked here writes `#<id>`, never
			// a name another card could spell. An element with no id yet (the
			// server gives every element one) is still written by name.
			key: id ? pathKey([`#${id}`]) : nameKey,
			path: id ? [`#${id}`] : [name],
			forceElement: id ? false : forceElement,
			// …and a condition saved naming it by name still opens as it
			// (findField's aliases), until a row on it is changed.
			aliases: id ? [nameKey] : [],
			// The id itself, as `findField` gives an `#id` field elsewhere
			// (kqlBuilder elementIdField): what the row names, by id.
			...(id ? { elementId: id } : {}),
			label,
			dataType,
			options,
			system: false,
			// The element's id (KPR-168): a condition saved naming it as
			// `#<id>` opens as this element (utils/kqlBuilder.js findField).
			definitions: id ? [{ id, label, dataType, options, fromSet: element?.fromSet || null, cardIds: [], cardLabels: [] }] : [],
		});
	});
	fields.sort((a, b) => a.label.localeCompare(b.label));
	// `systemFields: false` (KPR-687): a repeating group ENTRY's condition reads
	// the entry's columns alone — an entry has no created, updated or tags of
	// its own, and the block it is stored in would read them as columns.
	return [...fields, ...(systemFields ? SYSTEM_CONDITION_FIELDS : []), ...(tagsField ? [tagsField] : [])];
}

// ------------------------------------------------------------- rows <-> KQL

let rowSeq = 0;
const nextRowId = () => `condition-row-${++rowSeq}`;

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm';
const usesTime = field => field?.dataType !== 'date';

/** Raw input value -> the KQL value node its field's type calls for. */
export function makeConditionValue(field, comparison, raw, { allowMe = false } = {}) {
	if (isNullaryOperator(comparison)) return undefined;
	// `is any of` / `is none of` (KPR-949): the values picked, as a list of the
	// same nodes `is` would write one of. Nothing picked is nothing yet.
	if (isListOperator(comparison)) {
		const entries = (Array.isArray(raw) ? raw : [raw])
			.map(entry => makeConditionValue(field, 'eq', entry, { allowMe }))
			.filter(Boolean);
		return entries.length ? kqlList(entries) : undefined;
	}
	if (raw === undefined || raw === null || raw === '') return undefined;
	const kind = conditionKindFor(field);
	// A person is an account id, written as the quoted string the API matches.
	// "Me" is the grammar's own keyword, written only where the host allows it
	// (KPR-143): the text "me" is a typed name and stays a string.
	if (kind === 'user') return allowMe && raw === ME_VALUE ? kqlMe() : kqlStr(String(raw));
	// A part's value is the canonical literal its picker holds: a weekday's
	// name (`mon`), an hour's or a month's number.
	if (kind === 'datePart') return typeof raw === 'number' && Number.isFinite(raw) ? kqlNum(raw) : kqlStr(String(raw));
	if (kind === 'boolean') {
		// The grammar's own bool literal (`paid = true`); yes/no/1/0 from a
		// hand-written rule read the same way the API's compiler reads them.
		if (typeof raw === 'boolean') return kqlBool(raw);
		const text = String(raw).trim().toLowerCase();
		if (text === 'true' || text === 'yes' || text === '1') return kqlBool(true);
		if (text === 'false' || text === 'no' || text === '0') return kqlBool(false);
		return undefined;
	}
	if (kind === 'number') {
		return Number.isFinite(Number(raw)) ? kqlNum(raw) : kqlStr(raw);
	}
	if (kind === 'measurement') {
		// The control's `{ value, unit }` goes out as ONE quoted string in
		// as-entered form (`weight > "20 lb"`) — what the API's parser reads. A
		// bare number keeps meaning the element's default unit; hand-typed text
		// stays as typed so an unreadable literal can be fixed, not lost.
		if (typeof raw === 'number') return Number.isFinite(raw) ? kqlNum(raw) : undefined;
		if (isMeasurementValue(raw)) {
			const text = formatEnteredMeasurement(raw);
			return text ? kqlStr(text) : undefined;
		}
		if (typeof raw === 'object') return undefined;
		return kqlStr(String(raw));
	}
	if (kind === 'currency') {
		// The control's `{ amount, currency }` goes out as ONE quoted string
		// WITH its code (`price > "20.00 USD"`) — the API refuses a bare
		// number on a multi-currency element. A bare number from a
		// hand-written rule and hand-typed text stay as they are.
		if (typeof raw === 'number') return Number.isFinite(raw) ? kqlNum(raw) : undefined;
		if (isCurrencyValue(raw)) {
			const text = currencyLiteral(raw);
			return text ? kqlStr(text) : undefined;
		}
		if (typeof raw === 'object') return undefined;
		return kqlStr(String(raw));
	}
	// Dates commit the grammar's own literal shapes — `2026-08-29` and
	// `2026-08-29T09:00` — never an ISO string with milliseconds and a zone,
	// which the lexer would refuse.
	if (kind === 'date') return kqlDate(String(raw));
	return kqlStr(String(raw));
}

/**
 * A row's value after its operator changes. Values travel between the ones
 * that mean the same kind of thing — is / is not <-> is any of / is none of
 * (one value becomes a list of one, a list gives up its first) — and nothing
 * else: a text match ("contains") is not a choice, so it is not carried into a
 * list, and a list is not carried into emptiness.
 */
export function retargetConditionValue(field, fromCmp, toCmp, value, options = {}) {
	// A person's nodes travel as they are: the keyword `me` and the text "me"
	// read alike as raw values, and a change of comparison must not turn one
	// into the other.
	if (conditionKindFor(field) === 'user' && value && !isNullaryOperator(toCmp)) {
		const members = value.type === 'list' ? value.v : [value];
		if (isListOperator(toCmp)) {
			return ['eq', 'ne', 'in', 'nin'].includes(fromCmp) && members.length ? kqlList(members) : undefined;
		}
		return members[0];
	}
	const raw = rawValueOf(value);
	if (isListOperator(toCmp)) {
		const carries = ['eq', 'ne', 'in', 'nin'].includes(fromCmp);
		return carries && raw !== undefined ? makeConditionValue(field, toCmp, Array.isArray(raw) ? raw : [raw], options) : undefined;
	}
	return makeConditionValue(field, toCmp, Array.isArray(raw) ? raw[0] : raw, options);
}

/** One row -> an AST node, or null when the row is not yet a usable condition. */
export function rowToNode(row) {
	if (!row || !Array.isArray(row.path) || !row.path.length) return null;
	// A group's `has an entry where…`: the filter builder's own writer, so the
	// same rows give the same text in both builders (null while no condition
	// under it is complete).
	if (isEntriesCmp(row.cmp)) return entriesNode(row);
	const options = { forceElement: !!row.forceElement };
	if (isNullaryOperator(row.cmp)) return kqlCmp(row.path, row.cmp, undefined, options);
	if (!row.value) return null;
	return kqlCmp(row.path, row.cmp, row.value, options);
}

/**
 * Rows -> canonical KQL text. '' when no row is complete. `rows` are the top
 * line's items — rows, and groups of rows (utils/conditionGroups.js) — joined
 * by `match`: 'all' (`and`, the default) or 'any' (`or`). Hand back the
 * `match` parseConditions gave with its rows: without it they join by `and`.
 */
export function rowsToKql(rows, match = MATCH_ALL) {
	const node = writeGrouped(rows, match, rowToNode);
	return node ? kqlSerialize(node) : '';
}

/**
 * Rows with every NAME that stands for one stored value replaced by that
 * value (ruled b01) — a choice's label by the choice's value, a tag's name
 * by its id — through the validator's own rule (`resolveQueryLabels`), one
 * row at a time so each keeps its id (and its input its focus). `{ rows,
 * changed }`; the same array back when nothing moved.
 */
export function resolveRowLabels(rows, fields) {
	let changed = false;
	const next = (rows || []).map(row => {
		// A group of rows (KPR-951): its rows by the same rule.
		if (isGroupItem(row)) {
			const inner = resolveRowLabels(row.rows || [], fields);
			if (!inner.changed) return row;
			changed = true;
			return { ...row, rows: inner.rows };
		}
		// The conditions under a group's `has an entry where…` read the group's
		// own columns (KPR-863), by the same rule.
		if (isEntriesCmp(row.cmp)) {
			const group = findField(fields, row.path, row.forceElement);
			const inner = resolveRowLabels(row.entryRows || [], entryColumnFields(group));
			if (!inner.changed) return row;
			changed = true;
			return { ...row, entryRows: inner.rows };
		}
		const node = rowToNode(row);
		if (!node || !node.value) return row;
		const { ast, changed: moved } = resolveQueryLabels(node, fields);
		if (!moved) return row;
		changed = true;
		return { ...row, value: ast.value };
	});
	return { rows: changed ? next : rows, changed };
}

// `in (...)`/`not in (...)` ARE cmp nodes. Since KPR-949 the operator Select
// offers them ("is any of" / "is none of") on a choice, a tag, a lookup and a
// part of a date, and the value is a multi-select of pills, so a stored one
// opens as a row there (`listRowNode`). On a field that offers no list (text,
// number, a measurement…) it is still Advanced-shaped: a row would show an
// out-of-vocabulary operator, and the first touch of it would rewrite the query
// into something narrower. A list of anything but plain strings and numbers
// (`me`, a date, a field reference) stays text for the same reason.
// A repeating group's block (`ingredient has (…)`, keepr-api KPR-681) is a
// condition whose value is a query: no row holds one, so it is Advanced too.
// What `g.count` is (the number of entries) has no row here either: the count
// is asked in the filter builder, and here it opens the text.
const isCountPath = path => Array.isArray(path) && path.length === 2 && path[1] === ENTRY_COUNT;
const isScalarMember = member => !!member && (member.type === 'str' || member.type === 'num');
const isMeMember = member => !!member && member.type === 'me';

/** Does this value name the caller, alone or among a list's members? */
const valueNamesMe = value => !!value && (isMeMember(value) || (value.type === 'list' && Array.isArray(value.v) && value.v.some(isMeMember)));

/**
 * Is this `in` / `not in` something a ROW shows? Only against a field of the
 * vocabulary (`fields`) whose kind offers the comparison, with at least one
 * member and every member a plain string or number (or, on a person where the
 * host allows it, `me`).
 */
const listRowNode = (node, fields, allowMe) => {
	if (!fields || !node || node.op !== 'cmp' || !isListOperator(node.cmp) || isCountPath(node.path)) return false;
	const members = node.value?.type === 'list' ? node.value.v : null;
	const kind = conditionKindFor(findField(fields, node.path, !!node.forceElement));
	const memberFits = member => isScalarMember(member) || (allowMe && kind === 'user' && isMeMember(member));
	if (!Array.isArray(members) || !members.length || !members.every(memberFits)) return false;
	return conditionOperatorValues(kind).includes(node.cmp);
};

/**
 * Is this condition something a ROW shows? (KPR-143) Two rules for a person:
 *  - `me` is a row only where the host allows it AND the field is a user
 *    element; on any other host, or against any other field, the query is the
 *    sentence's (a row would show a bare keyword, and the first edit would
 *    drop it). Nothing is written by showing it.
 *  - a comparison the person kind does not offer (`contains`, `>`) is text,
 *    as every list the kind does not offer is.
 */
const isConditionNode = (node, fields, allowMe = false) => {
	if (!node || node.op !== 'cmp' || node.cmp === BLOCK_CMP || isCountPath(node.path)) return false;
	const kind = fields ? conditionKindFor(findField(fields, node.path, !!node.forceElement)) : null;
	if (valueNamesMe(node.value) && !(allowMe && kind === 'user')) return false;
	if (kind === 'user' && !conditionOperatorValues('user').includes(node.cmp)) return false;
	return isListOperator(node.cmp) ? listRowNode(node, fields, allowMe) : true;
};

/**
 * A top-level node as a repeating group's row — `g has (…)`, `not g has (…)`
 * (KPR-863; the filter builder's own reader, entriesRowOf) — or null. Only when
 * every condition inside is one this builder can show: an `or`, a nested block
 * or a count inside stays the text's, and so does a list inside unless the
 * group's own column offers one (KPR-949: a choice column's "is any of" — the
 * entry panel offers it, so what it writes must open again as rows).
 */
const entriesRow = (node, fields, allowMe) => {
	const row = entriesRowOf(node);
	if (!row) return null;
	const columns = fields ? entryColumnFields(findField(fields, row.path, row.forceElement)) : null;
	return row.entryRows.every(entry => isConditionNode({ op: 'cmp', ...entry }, columns, allowMe)) ? row : null;
};
const isRowNode = (node, fields, allowMe) => isConditionNode(node, fields, allowMe) || !!entriesRow(node, fields, allowMe);

/** One node as the row that shows it, or null when no row does. */
const rowOfNode = (node, fields, allowMe) => {
	if (!isRowNode(node, fields, allowMe)) return null;
	return entriesRow(node, fields, allowMe) || {
		id: nextRowId(),
		path: [...node.path],
		forceElement: !!node.forceElement,
		cmp: node.cmp,
		value: node.value,
	};
};

/**
 * KQL text -> `{ rows, match, error }`. `rows` are the top line's items: rows,
 * and groups of rows one level down (`{ id, group: true, match, rows }`,
 * utils/conditionGroups.js); `match` is how the top line joins them, 'all' or
 * 'any'. `rows` is null when the query parses but is deeper than one level of
 * groups, or holds a condition no row shows — the sentence signal, not a
 * failure. `fields` (buildConditionFields' answer, optional) lets an `is any
 * of` / `is none of` on a field that offers it open as a row; without it none
 * does. `allowMe` (KPR-143) is the host's: only then does a person's `me` open
 * as a row; otherwise a query naming `me` is the sentence's, whole.
 */
export function parseConditions(text, fields = null, { allowMe = false } = {}) {
	const trimmed = (text || '').trim();
	if (!trimmed) return { rows: [], match: MATCH_ALL, error: null };
	let ast;
	try {
		ast = kqlParse(trimmed);
	} catch (err) {
		return { rows: null, match: MATCH_ALL, error: err?.message || 'This condition could not be read.' };
	}
	const tree = readGrouped(ast, node => rowOfNode(node, fields, allowMe));
	if (!tree) return { rows: null, match: MATCH_ALL, error: null };
	// An `or` or a group that names something the vocabulary does not hold —
	// what "any of these cards" stores is an `or` over the same element on each
	// card, `#a = open or #b = open` — stays the sentence's: its rows would show
	// a bare id, and the first edit could rewrite one card's half. (A plain
	// `and` of such rows opens as rows, with the stray named, as it always has.)
	const joined = tree.match === MATCH_ANY || tree.items.some(isGroupItem);
	if (joined && fields && allRows(tree.items).some(row => findField(fields, row.path, !!row.forceElement).unknown)) {
		return { rows: null, match: MATCH_ALL, error: null };
	}
	return { rows: tree.items, match: tree.match, error: null };
}

// ------------------------------------------------------------------- checks

/** Parse without throwing: `{ ast }`, null when the text does not read. */
function parseLoose(text) {
	const trimmed = (text || '').trim();
	if (!trimmed) return { ast: null };
	try {
		return { ast: kqlParse(trimmed) };
	} catch (err) {
		return { ast: null };
	}
}

/** Every condition node in the text, wherever it sits ([] when it does not read). */
function parseConditionsLoose(text) {
	const { ast } = parseLoose(text);
	const out = [];
	const walk = node => {
		if (!node) return;
		if (node.op === 'cmp') { out.push(node); return; }
		(node.args || []).forEach(walk);
		if (node.arg) walk(node.arg);
	};
	walk(ast);
	return out;
}

/**
 * The text mode's checks (utils/idValueValidation.js): every id-backed value
 * in the text, with its field's label. A choice's or a tag's name is noted:
 * the box keeps what was typed, and saving stores the choice or tag itself.
 */
function queryChecks(text, fields, userVocabulary) {
	const { ast } = parseLoose(text);
	if (!ast) return [];
	// An operator the API refuses (`tags ~ x`, ranges on tags — T7, ruled
	// b26) first: a rule saved with one is refused. Then the values: a choice's
	// name or a tag's name is stored as the choice or the id (ruled b01).
	return [
		...checkQueryOperators(ast, fields).map(check => ({ ...check, fieldLabel: check.field.label })),
		...checkQueryValues(ast, fields, { vocabularies: { user: userVocabulary } })
			.map(check => ({ ...check, fieldLabel: check.field.label, result: displayedCheck(check.result, { labels: 'note', action: 'save' }) }))
			.filter(check => check.result),
	];
}

// ------------------------------------------------------------------- render

/** The pills a multi-select shows for a stored list: each value once (a stored `in (a, a)` keeps both until it is edited). */
const pickedValues = raw => (Array.isArray(raw) ? raw.filter((value, index) => raw.indexOf(value) === index) : []);

const fieldIcon = field => getDictionaryFromName(field?.dataType)?.bsIconJSX || null;

/** The words of a join (KPR-951): "Match all of these", "Or none of these". */
const MATCH_WORDS = { [MATCH_ALL]: 'all', [MATCH_ANY]: 'any', [MATCH_NONE]: 'none' };
const TOP_MATCH_OPTIONS = TOP_MATCHES.map(value => ({ value, label: MATCH_WORDS[value] }));
const GROUP_MATCH_OPTIONS = GROUP_MATCHES.map(value => ({ value, label: MATCH_WORDS[value] }));

/** Why `me` is not available, in the rules' words (a host may say its own). */
export const DEFAULT_ME_REASON = 'This condition names “me”, and “me” has no one to mean here: it is checked when nobody is signed in. Pick the person instead.';

export default function ConditionBuilder({
	value = '',
	onChange,
	elements = [],
	collectionId,
	disabled = false,
	tagDisabledFor,
	// false: offer the elements alone — no created, updated or tags (a
	// repeating group entry's condition, KPR-687's rule editor).
	systemFields = true,
	// What an empty builder says — an entry's condition (KPR-836) counts
	// entries, not items.
	emptyText = 'No conditions — every item of this card counts.',
	// optional; `(text) => void`. A host that shows the text its own way (the
	// driven value's, with every #id explained beneath it: cards/
	// DrivenWhereEditor, KPR-950) takes "Edit as text" itself, with the text
	// the rows say; without it the builder opens its own text mode.
	onEditAsText,
	// false: "Me" is not offered, and a condition that names it opens as the
	// read-only sentence. A rule runs with nobody signed in, so the API refuses
	// `me` in one (keepr-api utils/automations/conditions.js); a host whose
	// condition is read by the person viewing it (a worklist, a multi add seed)
	// passes true (KPR-143).
	allowMe = false,
	// optional; what the sentence says when the condition names `me` and the
	// host does not allow it. The default is the rules' reason.
	meReason = DEFAULT_ME_REASON,
}) {
	// The top line's items — rows, and groups of rows (KPR-951) — and how it
	// joins them: 'all' (`and`) or 'any' (`or`).
	const [rows, setRows] = useState([]);
	const [match, setMatchState] = useState(MATCH_ALL);
	const matchRef = useRef(MATCH_ALL);
	const setMatch = useCallback((next) => { matchRef.current = next; setMatchState(next); }, []);
	const [advanced, setAdvanced] = useState(false);
	const [rawText, setRawText] = useState(value || '');
	const [error, setError] = useState(null);
	const [switchFailed, setSwitchFailed] = useState(false);
	// Advanced because the query needs it, or because the user asked for it?
	// Only the first is worth an explanation.
	const [manualAdvanced, setManualAdvanced] = useState(false);
	// What each lookup row's item picker holds (row + field), for the check.
	const [lookupVocabularies, setLookupVocabularies] = useState({});

	// TAGS (docs/plans/tags.md §3.7, §5.2). A condition is stored and shared,
	// so its `tags` field is the COLLECTION mode: the collection's tags and
	// item tags, by id — a private tag is never offered, and one named here
	// (typed in the text) is flagged and says why. The vocabulary is asked
	// for only once a condition names `tags` (most rules never do); my private
	// tags only to recognise one.
	const namesTags = useMemo(() => {
		const hit = node => !node?.forceElement && Array.isArray(node?.path) && node.path[0] === TAG_FIELD_KEY;
		return allRows(rows).some(hit) || parseConditionsLoose(rawText).some(hit);
	}, [rows, rawText]);
	const { tags: collectionTags, loaded: tagsLoaded } = useCollectionTags(collectionId, { enabled: Boolean(collectionId) && namesTags });
	const myTags = useOptionalTags()?.tags || null;
	const tagsField = useMemo(() => (collectionId && systemFields ? tagsFilterField({
		tags: collectionTags,
		privateTags: myTags,
		mode: 'collection',
		loaded: tagsLoaded,
	}) : null), [collectionId, collectionTags, myTags, tagsLoaded, systemFields]);
	const fields = useMemo(() => buildConditionFields(elements, { tagsField, systemFields }), [elements, tagsField, systemFields]);

	// What we last handed to onChange. The parent echoes it straight back as
	// `value`; re-parsing our own output would rebuild every row (new ids) and
	// yank focus out of whatever input the user is mid-edit in.
	const lastEmitted = useRef(null);
	// The text the parent HOLDS: what it last handed us, or what we last
	// handed it. The names below are mapped against this, so a rule with
	// nothing to map is never written back by being opened.
	const held = useRef(value || '');

	// The vocabulary the next parse reads an `is any of` against (KPR-949).
	const fieldsRef = useRef(fields);
	fieldsRef.current = fields;
	const allowMeRef = useRef(allowMe);
	allowMeRef.current = allowMe;
	// The text the PARENT last handed us (not what we handed it).
	const received = useRef(value || '');

	useEffect(() => {
		if (value === lastEmitted.current) return;
		held.current = value || '';
		received.current = value || '';
		const parsed = parseConditions(value, fieldsRef.current, { allowMe: allowMeRef.current });
		setRawText(value || '');
		setSwitchFailed(false);
		setError(parsed.error || null);
		if (parsed.rows === null) {
			setAdvanced(true);
			setManualAdvanced(false);
			setRows([]);
			return;
		}
		setAdvanced(false);
		setManualAdvanced(false);
		setRows(parsed.rows);
		setMatch(parsed.match);
	}, [value, setMatch]);

	// A condition that opened as text only because the card's elements had not
	// arrived yet — an `is any of` is a row only against a field that offers it
	// (KPR-949) — opens as rows once they have. Only the text the parent handed
	// us, untouched: what a person typed, or asked to see as text, stays text.
	const textState = useRef({});
	textState.current = { advanced, manualAdvanced, error, rawText };
	useEffect(() => {
		const { advanced: asText, manualAdvanced: asked, error: unreadable, rawText: shown } = textState.current;
		if (!asText || asked || unreadable || shown !== received.current) return;
		const parsed = parseConditions(shown, fields, { allowMe });
		if (parsed.rows === null) return;
		setSwitchFailed(false);
		setAdvanced(false);
		setRows(parsed.rows);
		setMatch(parsed.match);
	}, [fields, allowMe, setMatch]);

	const emit = useCallback(next => {
		lastEmitted.current = next;
		held.current = next;
		if (typeof onChange === 'function') onChange(next);
	}, [onChange]);

	// `nextMatch` only when the top line's join changes; otherwise it stays.
	const commitRows = useCallback((nextRows, nextMatch = matchRef.current) => {
		setRows(nextRows);
		if (nextMatch !== matchRef.current) setMatch(nextMatch);
		const text = rowsToKql(nextRows, nextMatch);
		setRawText(text);
		setError(null);
		emit(text);
	}, [emit, setMatch]);

	// NAMES BECOME WHAT THEY STAND FOR (ruled b01). Whenever the rows, the
	// text or the vocabulary change — a stored rule opened, a text typed, the
	// collection's tags arriving — a choice's label or a tag's name is
	// replaced by its value, through the validator's rule. Rows are replaced
	// in place (and handed on); the text mode's box keeps what was typed and
	// hands on the mapped text. Nothing is written when nothing maps.
	useEffect(() => {
		if (advanced) {
			if (error) return;
			const next = resolveQueryTextLabels(rawText, fields);
			if (next !== rawText && next !== held.current) emit(next);
			return;
		}
		const { rows: next, changed } = resolveRowLabels(rows, fields);
		if (changed) commitRows(next);
	}, [advanced, error, rawText, rows, fields, emit, commitRows]);

	const fieldForRow = useCallback(row => findField(fields, row.path, row.forceElement), [fields]);

	// A person is picked here (KPR-143), and a typed name — in a stored rule
	// or in the text — is checked against the collaborators: asked for only
	// when a row, or the text, names a user element at all.
	const namesUserElement = useMemo(() => {
		if (!fields.some(field => field.dataType === 'user')) return false;
		if (advanced) {
			const parsed = parseConditionsLoose(rawText);
			return parsed.some(node => findField(fields, node.path || [], !!node.forceElement).dataType === 'user');
		}
		return allRows(rows).some(row => fieldForRow(row).dataType === 'user');
	}, [fields, advanced, rawText, rows, fieldForRow]);
	const { users: collaborators } = useCollaborators(namesUserElement ? collectionId : null);
	const userVocabulary = useMemo(() => userVocabularyOf(collaborators), [collaborators]);

	const rememberLookupVocabulary = useCallback((key, vocabulary) => {
		setLookupVocabularies(prev => (prev[key] === vocabulary ? prev : { ...prev, [key]: vocabulary }));
	}, []);

	const rowChecks = (row, field) => {
		const refused = checkOperator(field, row.cmp);
		if (refused) return [refused];
		if (!row.value) return [];
		const kind = idValueKindFor(field);
		if (!kind) return [];
		const vocabulary = kind === 'lookup'
			? lookupVocabularies[`${row.id}:${field.key}:${isListOperator(row.cmp) ? 'many' : 'one'}`]
			: (kind === 'user' ? userVocabulary : undefined);
		return checkConditionValues(field, row.cmp, row.value, { vocabulary })
			.map(check => ({ ...check, result: displayedCheck(check.result) }))
			.filter(check => check.result);
	};

	const fieldOptions = useMemo(() => {
		const known = new Set(fields.map(field => field.key));
		// A row whose field is not in this card's vocabulary (a rule authored
		// against another card, a dot-walk) still needs a readable option, or the
		// select would render a bare slug and invite an accidental overwrite.
		const strayByKey = new Map();
		allRows(rows)
			.map(row => fieldForRow(row))
			// (`tags` named without a collection to build its field from is one
			// of these too — findField's stand-in, not unknown.)
			.filter(field => !known.has(field.key))
			// Deduped: two rows on the same unknown field must not produce
			// duplicate Select values (and React key warnings).
			.forEach(field => { if (!strayByKey.has(field.key)) strayByKey.set(field.key, field); });
		return [...fields, ...strayByKey.values()].map(field => ({
			value: field.key,
			label: (
				<span className="condition-builder__field-option">
					{fieldIcon(field)}{fieldIcon(field) ? ' ' : ''}{field.label}
					{field.unknown ? ' (not on this card)' : ''}
				</span>
			),
			// The label is a ReactNode, so the typeahead reads this instead.
			searchText: field.label,
		}));
	}, [fields, rows, fieldForRow]);

	// A row is found by its id wherever it sits: on the top line or in a group
	// of rows (KPR-951, utils/conditionGroups.js mapRows).
	const updateRow = (rowId, patch) => {
		commitRows(mapRows(rows, row => (row.id === rowId ? { ...row, ...patch } : row)));
	};
	/** One row under a repeating group's `has an entry where…`, edited in its parent's `entryRows`. */
	const updateEntryRow = (parentId, entryRowId, patch) => {
		commitRows(mapRows(rows, row => (row.id === parentId
			? { ...row, entryRows: (row.entryRows || []).map(entry => (entry.id === entryRowId ? { ...entry, ...patch } : entry)) }
			: row)));
	};

	// WHERE A ROW LIVES (KPR-863, as in the filter builder). A row of the
	// condition reads its field in the card's vocabulary (`topScope`); a row
	// UNDER a repeating group's `has an entry where…` reads it in that group's
	// columns, those this person may read, and edits itself inside its
	// parent's `entryRows`. Rebuilt every render: `update` closes over this
	// render's rows.
	const topScope = {
		nested: false,
		fields,
		fieldFor: fieldForRow,
		update: (row, patch) => updateRow(row.id, patch),
		label: index => `Condition ${index + 1}`,
		// How this scope's rows join: the top line's all / any.
		join: match === MATCH_ANY ? 'Or' : 'And',
	};
	/** The rows of a group of conditions (KPR-951): the card's vocabulary, named by their group. */
	const groupScopeFor = (conditionGroup, groupIndex) => ({
		...topScope,
		conditionGroup,
		label: index => `Group ${groupIndex + 1} condition ${index + 1}`,
		join: conditionGroup.match === MATCH_ALL ? 'And' : 'Or',
	});
	const entryScopeFor = (parent, group) => {
		const columns = entryColumnFields(group);
		const offered = new Map();
		columns.forEach(column => elementFieldEntries(column, columns).forEach(entry => offered.set(entry.key, entry)));
		return {
			nested: true,
			parent,
			group,
			fields: columns,
			offered,
			fieldFor: row => findField(columns, row.path, row.forceElement),
			update: (row, patch) => updateEntryRow(parent.id, row.id, patch),
			label: index => `${group.label} condition ${index + 1}`,
		};
	};

	const handleFieldChange = (row, nextKey, scope = topScope) => {
		const next = scope.nested
			? (scope.offered.get(nextKey)?.field || scope.fieldFor(row))
			: [...fields, fieldForRow(row)].find(field => field.key === nextKey);
		if (!next) return;
		const operators = operatorsForField(next);
		const keepsOperator = operators.some(option => option.value === row.cmp);
		scope.update(row, {
			path: [...next.path],
			forceElement: !!next.forceElement,
			cmp: keepsOperator ? row.cmp : operators[0].value,
			// A value rarely survives a change of field type; carrying a number
			// into a choice would emit a condition that silently matches nothing.
			value: undefined,
			// A row moved ONTO a repeating group starts with its first column's
			// row under it; one moved off a group drops what was under it.
			entryRows: next.dataType === REPEATING_GROUP ? seedRowsForGroup(next) : undefined,
		});
	};

	const handleOperatorChange = (row, nextCmp, scope = topScope) => {
		const field = scope.fieldFor(row);
		scope.update(row, {
			cmp: nextCmp,
			value: retargetConditionValue(field, row.cmp, nextCmp, row.value, { allowMe }),
			// `has no entries` / `has entries` carry no conditions; the rows
			// come back (seeded) when the person asks about an entry again.
			entryRows: isEntriesCmp(nextCmp) ? (row.entryRows?.length ? row.entryRows : seedRowsForGroup(field)) : undefined,
		});
	};

	const handleValueChange = (row, raw, scope = topScope) => {
		const field = scope.fieldFor(row);
		scope.update(row, { value: makeConditionValue(field, row.cmp, raw, { allowMe }) });
	};

	/** A one-click match: it replaces the value it was offered for, and in a list only that member. */
	const handlePickMatch = (row, next, check, scope = topScope) => {
		const raw = rawValueOf(row.value);
		if (check?.member !== null && check?.member !== undefined && Array.isArray(raw)) {
			const swapped = raw.map((entry, index) => (index === check.member ? next : entry));
			handleValueChange(row, swapped.filter((entry, index) => swapped.indexOf(entry) === index), scope);
			return;
		}
		handleValueChange(row, next, scope);
	};

	const handleAddEntryRow = (parent, group) => {
		const added = newEntryRowFor(group);
		if (!added) return;
		commitRows(mapRows(rows, row => (row.id === parent.id ? { ...row, entryRows: [...(row.entryRows || []), added] } : row)));
	};

	const handleRemoveEntryRow = (parent, entryRowId) => {
		commitRows(mapRows(rows, row => (row.id === parent.id ? { ...row, entryRows: (row.entryRows || []).filter(entry => entry.id !== entryRowId) } : row)));
	};

	/** A new row on the card's first field, ready to fill in (null with no field to offer). */
	const newRow = () => {
		const first = fields[0];
		if (!first) return null;
		const [operator] = operatorsForField(first);
		return {
			id: nextRowId(),
			path: [...first.path],
			forceElement: !!first.forceElement,
			cmp: operator ? operator.value : 'eq',
			value: undefined,
			...(first.dataType === REPEATING_GROUP ? { entryRows: seedRowsForGroup(first) } : {}),
		};
	};

	const handleAddRow = () => {
		const added = newRow();
		if (added) commitRows([...rows, added]);
	};

	const handleRemoveRow = rowId => commitRows(removeRow(rows, rowId));

	// GROUPS OF ROWS (KPR-951). A group starts with one row, joined the other
	// way from the top line (a group that joins like its parent adds nothing).
	const handleAddGroup = () => {
		const added = newRow();
		if (added) commitRows([...rows, makeGroup(groupMatchFor(match), [added])]);
	};
	const handleAddGroupRow = (groupId) => {
		const added = newRow();
		if (added) commitRows(updateGroup(rows, groupId, conditionGroup => ({ ...conditionGroup, rows: [...(conditionGroup.rows || []), added] })));
	};
	const handleGroupMatch = (groupId, next) => {
		commitRows(updateGroup(rows, groupId, conditionGroup => ({ ...conditionGroup, match: next })));
	};
	const handleRemoveGroup = groupId => commitRows(rows.filter(item => item.id !== groupId));
	const handleTopMatch = next => commitRows(rows, next);

	const handleRawChange = text => {
		setRawText(text);
		setSwitchFailed(false);
		const parsed = parseConditions(text);
		setError(parsed.error || null);
		// The box is never rewritten under the cursor, and the text goes out
		// as typed — except that a name standing for one stored value goes out
		// as that value (ruled b01; the rest of the query is re-serialized
		// through the grammar only then, which keeps what it says).
		emit(parsed.error ? text : resolveQueryTextLabels(text, fields));
	};

	const handleSwitchToBuilder = () => {
		const parsed = parseConditions(rawText, fields, { allowMe });
		if (parsed.rows === null) { setSwitchFailed(true); setError(parsed.error || null); return; }
		setSwitchFailed(false);
		setError(null);
		setAdvanced(false);
		setManualAdvanced(false);
		setRows(parsed.rows);
		setMatch(parsed.match);
	};

	// "Edit as text": the host's own text view when it has one (KPR-950),
	// else this builder's text mode, holding exactly the text it was shown.
	const openText = (text) => {
		if (typeof onEditAsText === 'function') { onEditAsText(text); return; }
		setRawText(text); setManualAdvanced(true); setAdvanced(true);
	};

	// A condition the rows cannot show, cleared on purpose: the builder starts
	// again from nothing (saved only when the host saves).
	const handleClearSentence = () => {
		setAdvanced(false);
		setManualAdvanced(false);
		setSwitchFailed(false);
		setError(null);
		setRows([]);
		setMatch(MATCH_ALL);
		setRawText('');
		emit('');
	};

	// `status` is the row's check ('warning' when a value names nothing).
	const renderValueInput = (row, field, index, status, scope = topScope) => {
		if (isNullaryOperator(row.cmp) || isEntriesCmp(row.cmp)) return null;
		const kind = conditionKindFor(field);
		const raw = rawValueOf(row.value);
		const valueLabel = `${scope.label(index)} value`;
		const handleChange = next => handleValueChange(row, next, scope);

		// A part of a date picks from its values the same way (Sunday … Saturday).
		if (kind === 'choice' || kind === 'datePart') {
			const options = buildChoiceOptions(field.options?.choices);
			// "Is any of" / "is none of": the choices picked, as pills (KPR-949).
			if (isListOperator(row.cmp)) {
				return (
					<Select
						mode="multiple"
						className="condition-builder__value-input"
						placeholder="Choose values"
						showSearch
						filterOption={optionSearchFilter}
						disabled={disabled}
						aria-label={valueLabel}
						value={pickedValues(raw)}
						status={status}
						options={options}
						onChange={next => handleChange(next)}
					/>
				);
			}
			return (
				<Select
					className="condition-builder__value-input"
					placeholder="Value"
					allowClear
					showSearch
					filterOption={optionSearchFilter}
					disabled={disabled}
					aria-label={valueLabel}
					value={raw ?? undefined}
					status={status}
					options={options}
					onChange={next => handleChange(next)}
				/>
			);
		}

		if (kind === 'tag') {
			const multiple = isListOperator(row.cmp);
			return (
				<TagSelect
					key={`${row.id}:${multiple ? 'many' : 'one'}`}
					className="condition-builder__value-input"
					multiple={multiple}
					value={multiple ? pickedValues(raw) : (raw ?? undefined)}
					collectionTags={field.options?.tags || []}
					privateTags={null}
					itemTitles={field.options?.itemTitles}
					disabledFor={tagDisabledFor}
					status={status}
					disabled={disabled}
					aria-label={valueLabel}
					placeholder={multiple ? 'Choose tags' : undefined}
					onChange={next => handleChange(next)}
				/>
			);
		}

		if (kind === 'boolean') {
			const { trueLabel, falseLabel } = booleanLabels(field);
			return (
				<Select
					className="condition-builder__value-input"
					placeholder="Yes or no"
					disabled={disabled}
					aria-label={valueLabel}
					value={typeof raw === 'boolean' ? raw : undefined}
					options={[{ value: true, label: trueLabel }, { value: false, label: falseLabel }]}
					onChange={next => handleChange(next)}
				/>
			);
		}

		if (kind === 'number') {
			return (
				<InputNumber
					className="condition-builder__value-input"
					placeholder="Value"
					disabled={disabled}
					aria-label={valueLabel}
					value={raw ?? null}
					onChange={next => handleChange(next)}
					// A typed fraction is the number it is, as on the item form (KPR-1025).
					parser={text => parseNumberEntry(text)}
					inputMode={getNumericInputMode(field?.dataType)}
				/>
			);
		}

		if (kind === 'measurement') {
			return (
				<MeasurementValueInput
					// Keyed by field: a row moved onto another element opens a
					// fresh control (and leaves any text-mode fallback behind).
					key={`${row.id}:${field.key}`}
					id={`condition-value-${row.id}`}
					className="condition-builder__value-input"
					raw={raw}
					options={field.options}
					name={field.path.join('.')}
					label={field.label}
					aria-label={valueLabel}
					disabled={disabled}
					onChange={next => handleChange(next)}
				/>
			);
		}

		if (kind === 'currency') {
			return (
				<CurrencyValueInput
					key={`${row.id}:${field.key}`}
					id={`condition-value-${row.id}`}
					className="condition-builder__value-input"
					raw={raw}
					options={field.options}
					name={field.path.join('.')}
					label={field.label}
					aria-label={valueLabel}
					disabled={disabled}
					onChange={next => handleChange(next)}
				/>
			);
		}

		if (kind === 'date') {
			const withTime = usesTime(field);
			const parsed = raw ? dayjs(raw) : null;
			return (
				<DatePicker
					className="condition-builder__value-input"
					disabled={disabled}
					aria-label={valueLabel}
					showTime={withTime ? { format: 'HH:mm' } : false}
					value={parsed && parsed.isValid() ? parsed : null}
					onChange={next => handleChange(
						next ? dayjs(next).format(withTime ? DATE_TIME_FORMAT : DATE_FORMAT) : undefined
					)}
				/>
			);
		}

		if (kind === 'user') {
			// A person is picked, never typed (KPR-143): the item form's own
			// picker, the collection's collaborators first. A rule stores the
			// account id; "Me" (the keyword) only where the host allows it. A
			// stored typed name shows as it was written, flagged beneath.
			const multiple = isListOperator(row.cmp);
			return (
				<UserPickerSelect
					key={`${row.id}:${field.key}:${multiple ? 'many' : 'one'}`}
					className="condition-builder__value-input"
					collectionId={collectionId}
					multiple={multiple}
					meMode={allowMe ? 'value' : null}
					onlyAccountIds
					value={multiple ? pickedValues(raw) : (raw ?? undefined)}
					status={status}
					disabled={disabled}
					aria-label={valueLabel}
					placeholder={multiple ? 'Pick people' : 'Pick a person'}
					onChange={next => handleChange(next)}
				/>
			);
		}

		if (kind === 'lookup') {
			const lookupCardId = normalizeId(field.options?.lookupCardId);
			const multiple = isListOperator(row.cmp);
			// No collection (a preview surface, a test) or a dangling lookup: a
			// text box that still edits the stored value beats a picker that can
			// never fill itself.
			if (collectionId && lookupCardId) {
				return (
					<ItemLookupSelect
						key={`${row.id}:${field.key}:${multiple ? 'many' : 'one'}`}
						className="condition-builder__value-input"
						collectionId={collectionId}
						cardId={lookupCardId}
						multiple={multiple}
						disabled={disabled}
						ariaLabel={valueLabel}
						value={multiple ? pickedValues(raw) : (raw ?? '')}
						status={status}
						onVocabulary={vocabulary => rememberLookupVocabulary(`${row.id}:${field.key}:${multiple ? 'many' : 'one'}`, vocabulary)}
						onChange={next => handleChange(next)}
					/>
				);
			}
			// A list with nothing to pick from: items typed one at a time, each a
			// pill (an id, or a title the API matches).
			if (multiple) {
				return (
					<Select
						mode="tags"
						className="condition-builder__value-input"
						placeholder="Item titles or ids"
						open={false}
						tokenSeparators={[',']}
						disabled={disabled}
						aria-label={valueLabel}
						value={pickedValues(raw)}
						status={status}
						onChange={next => handleChange(next)}
					/>
				);
			}
		}

		return (
			<Input
				{...NO_TEXT_ASSIST}
				className="condition-builder__value-input"
				disabled={disabled}
				aria-label={valueLabel}
				value={raw ?? ''}
				status={status}
				placeholder={kind === 'lookup' ? 'Item title or id' : 'Value'}
				onChange={event => handleChange(event.target.value)}
			/>
		);
	};

	// The text mode checks the text itself, each line naming its field. A
	// lookup's title is noted without items to offer (no picker is open).
	const textChecks = advanced && !error ? queryChecks(rawText, fields, userVocabulary) : [];
	// `me` typed or stored where the host refuses it (KPR-143): said in words,
	// never silently dropped and never silently saved.
	const namesRefusedMe = advanced && !error && !allowMe && queryUsesMe(rawText);
	const handlePickTextMatch = (next, check) => {
		const { ast } = parseLoose(rawText);
		if (!ast) return;
		handleRawChange(kqlSerialize(replaceQueryValue(ast, check.at, next)));
	};

	// A condition that READS but is more than the rows show (a group inside a
	// group, `not (a and b)`, a list on a field that offers none…): one plain
	// sentence, read-only, with Edit as text (platform-standards §15). Nothing
	// is written by showing it.
	if (advanced && !manualAdvanced && !error) {
		const sentence = conditionSentence(rawText, fields);
		return (
			<div className="condition-builder condition-builder__sentence-view">
				<p className="condition-builder__sentence" aria-label="The condition">{sentence ? `Where ${sentence}.` : ''}</p>
				<Typography.Text type="secondary" className="d-block">
					The builder can’t show this condition as rows. It is kept exactly as it is unless you change it.
				</Typography.Text>
				{namesRefusedMe ? <Typography.Text type="warning" className="d-block condition-builder__me-reason">{meReason}</Typography.Text> : null}
				<IdValueCheck checks={textChecks} disabled={disabled} onPick={handlePickTextMatch} />
				<Space wrap className="condition-builder__actions">
					<Button disabled={disabled} onClick={() => openText(rawText)}>Edit as text</Button>
					<Button type="link" disabled={disabled} onClick={handleClearSentence}>Clear it and use the builder</Button>
				</Space>
			</div>
		);
	}

	if (advanced) {
		return (
			<div className="condition-builder condition-builder__advanced">
				<Alert
					type={error ? 'error' : 'info'}
					showIcon
					message={error
						? `This condition could not be read: ${error}`
						: 'Editing the condition as KQL text.'}
					description={switchFailed
						? 'Still not something the builder can show as rows — keep it as text, or simplify it to conditions and one level of groups.'
						: 'Edit it as text below. A choice or tag given by its name is saved as the choice or tag itself.'}
					action={(
						<Button size="small" disabled={disabled} onClick={handleSwitchToBuilder}>
							Switch to builder
						</Button>
					)}
				/>
				<Input
					{...NO_TEXT_ASSIST}
					className="condition-builder__raw"
					aria-label="Condition query"
					disabled={disabled}
					value={rawText}
					placeholder={'resident = "Molly" and created > -3mo'}
					onChange={event => handleRawChange(event.target.value)}
				/>
				{namesRefusedMe ? <Typography.Text type="warning" className="d-block condition-builder__me-reason">{meReason}</Typography.Text> : null}
				<IdValueCheck checks={textChecks} disabled={disabled} onPick={handlePickTextMatch} />
				{/* Saved, the text names elements, cards and tags by id: each said in
				    words beneath it (KPR-952), as the filter builder's text view does. */}
				<FilterIdsLine text={rawText} fields={fields} />
				<CopyTextButton text={rawText} size="small" />
			</div>
		);
	}

	/** The field menu of a scope: the card's, or (nested) the group's columns, plus any column a row still names that it no longer offers. */
	const optionsForScope = (scope, scopeRows) => {
		if (!scope.nested) return fieldOptions;
		const options = [...scope.offered.values()].map(entry => ({ value: entry.key, label: entry.label, searchText: entry.label }));
		const known = new Set(options.map(option => option.value));
		(scopeRows || []).forEach((entry) => {
			const stray = scope.fieldFor(entry);
			if (known.has(stray.key)) return;
			known.add(stray.key);
			options.push({ value: stray.key, label: stray.label, searchText: stray.label });
		});
		return options;
	};

	/** The conditions under a group's `has an entry where…`: one panel, because they must hold on ONE entry. */
	const renderEntryPanel = (row, group) => {
		const scope = entryScopeFor(row, group);
		return (
			<div className="condition-builder__entry" role="group" aria-label={`Conditions on one entry of ${group.label}`}>
				<p className="condition-builder__entry-rule">
					{row.cmp === 'nhas' ? 'No entry matches all of these' : 'One entry matches all of these'}
				</p>
				{(row.entryRows || []).map((entry, entryIndex) => renderRow(entry, entryIndex, scope, row.entryRows))}
				{!scope.fields.length ? <p className="condition-builder__empty">This group has no column you can condition on.</p> : null}
				<Button
					className="condition-builder__entry-add"
					size="small"
					icon={<PLUS />}
					disabled={disabled || !scope.fields.length}
					onClick={() => handleAddEntryRow(row, group)}
				>
					Add condition on the same entry
				</Button>
			</div>
		);
	};

	const renderRow = (row, index, scope = topScope, siblings = rows) => {
		const field = scope.fieldFor(row);
		const checks = rowChecks(row, field);
		const status = checks.some(check => isFlagged(check.result)) ? 'warning' : undefined;
		const name = scope.label(index);
		return (
			<div className="condition-builder__block" key={row.id}>
				<div className={`condition-builder__row${scope.nested ? ' condition-builder__row--entry' : ''}`}>
					<span className="condition-builder__join">{index === 0 && !scope.nested ? 'Where' : (scope.join || 'And')}</span>
					<Select
						className="condition-builder__field"
						showSearch
						filterOption={optionSearchFilter}
						placeholder={scope.nested ? 'Column' : 'Field'}
						disabled={disabled}
						aria-label={`${name} ${scope.nested ? 'column' : 'field'}`}
						// The field the row reads as: a condition saved by
						// an element's name shows that element, offered by id.
						value={field.key}
						options={optionsForScope(scope, siblings)}
						onChange={next => handleFieldChange(row, next, scope)}
					/>
					<Select
						className="condition-builder__operator"
						disabled={disabled}
						aria-label={`${name} operator`}
						value={row.cmp}
						options={operatorsForField(field)}
						onChange={next => handleOperatorChange(row, next, scope)}
					/>
					<div className={`condition-builder__value${isListOperator(row.cmp) ? ' condition-builder__value--list' : ''}`}>
						{renderValueInput(row, field, index, status, scope)}
						<IdValueCheck
							checks={checks}
							disabled={disabled}
							onPick={(next, check) => handlePickMatch(row, next, check, scope)}
						/>
					</div>
					<Button
						type="text"
						icon={<CLOSE />}
						disabled={disabled}
						onClick={() => (scope.nested ? handleRemoveEntryRow(scope.parent, row.id) : handleRemoveRow(row.id))}
						aria-label={`Remove ${name.charAt(0).toLowerCase()}${name.slice(1)}`}
					/>
				</div>
				{isEntriesCmp(row.cmp) && field.dataType === REPEATING_GROUP ? renderEntryPanel(row, field) : null}
			</div>
		);
	};

	/**
	 * A group of conditions (KPR-951): its own all / any / none, its rows, and
	 * its own Add. One level only: a group holds rows, never a group.
	 */
	const renderGroup = (conditionGroup, index, groupIndex) => {
		const scope = groupScopeFor(conditionGroup, groupIndex);
		const name = `Group ${groupIndex + 1}`;
		const groupRows = conditionGroup.rows || [];
		return (
			<div className="condition-builder__group" role="group" aria-label={`${name}: ${MATCH_WORDS[conditionGroup.match]} of these`} key={conditionGroup.id}>
				<div className="condition-builder__group-head">
					<span className="condition-builder__join">{index === 0 ? 'Where' : topScope.join}</span>
					<Select
						className="condition-builder__match-select"
						disabled={disabled}
						aria-label={`${name} matches`}
						value={conditionGroup.match}
						options={GROUP_MATCH_OPTIONS}
						onChange={next => handleGroupMatch(conditionGroup.id, next)}
					/>
					<span className="condition-builder__match-words">of these</span>
					<Button
						className="condition-builder__group-remove"
						type="text"
						icon={<CLOSE />}
						disabled={disabled}
						onClick={() => handleRemoveGroup(conditionGroup.id)}
						aria-label={`Remove group ${groupIndex + 1}`}
					/>
				</div>
				{groupRows.map((row, rowIndex) => renderRow(row, rowIndex, scope, groupRows))}
				{groupRows.length ? null : <p className="condition-builder__empty">No conditions in this group yet.</p>}
				<Button
					className="condition-builder__group-add"
					size="small"
					icon={<PLUS />}
					disabled={disabled || !fields.length}
					onClick={() => handleAddGroupRow(conditionGroup.id)}
				>
					Add condition to the group
				</Button>
			</div>
		);
	};

	let groupCount = 0;
	return (
		<div className="condition-builder">
			{/* The top line, once there are two things to join. */}
			{rows.length > 1 ? (
				<div className="condition-builder__match">
					<span className="condition-builder__match-words">Match</span>
					<Select
						className="condition-builder__match-select"
						disabled={disabled}
						aria-label="Match all or any of these"
						value={match}
						options={TOP_MATCH_OPTIONS}
						onChange={handleTopMatch}
					/>
					<span className="condition-builder__match-words">of these</span>
				</div>
			) : null}

			{rows.map((item, index) => (isGroupItem(item)
				? renderGroup(item, index, groupCount++)
				: renderRow(item, index)))}

			{rows.length ? null : (
				<p className="condition-builder__empty">
					{emptyText}
				</p>
			)}

			<Space wrap className="condition-builder__actions">
				<Button
					icon={<PLUS />}
					disabled={disabled || !fields.length}
					onClick={handleAddRow}
				>
					Add condition
				</Button>
				<Button
					className="condition-builder__add-group"
					icon={<PLUS />}
					disabled={disabled || !fields.length}
					onClick={handleAddGroup}
				>
					Add a group
				</Button>
				<Button
					type="link"
					size="small"
					disabled={disabled}
					onClick={() => openText(rowsToKql(rows, match))}
				>
					Edit as text
				</Button>
			</Space>
		</div>
	);
}
